import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { MapPin, X, ChevronLeft, ChevronRight, Eye, Folder, FolderOpen, ChevronDown } from 'lucide-react';
import { getProjects } from '../data/api';

const PortfolioProfessional = () => {
  const [activeCategory, setActiveCategory] = useState('hospitality');
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [allProjects, setAllProjects] = useState([]);

  useEffect(() => {
    getProjects().then(setAllProjects).catch(() => {});
  }, []);

  const categories = [
    { id: 'residential', label: 'Residential', color: 'bg-emerald-500' },
    { id: 'hospitality', label: 'Hospitality', color: 'bg-orange-500' },
    { id: 'office', label: 'Office', color: 'bg-blue-500' },
    { id: 'retail', label: 'Retail', color: 'bg-pink-500' },
    { id: 'industrial', label: 'Industrial', color: 'bg-amber-500' }
  ];

  const getProjectsByCategory = (categoryId) => {
    return allProjects.filter(p => p.category === categoryId);
  };

  const activeProjects = getProjectsByCategory(activeCategory);
  const activeCategoryData = categories.find(c => c.id === activeCategory);

  const openGallery = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };

  const closeGallery = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) => 
        prev === selectedProject.galleryImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedProject.galleryImages.length - 1 : prev - 1
      );
    }
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="portfolio" className="min-h-screen bg-gray-900 flex flex-col lg:flex-row">
      {/* Left Side - Categories & Project List */}
      <div className="w-full lg:w-1/3 bg-gray-900 p-4 sm:p-6 lg:p-8 flex flex-col">
        <div className="mb-4 sm:mb-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">Our Portfolio</h2>
          <p className="text-sm sm:text-base text-gray-400">Explore our projects by category</p>
        </div>
        
        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-4 sm:mb-6 overflow-x-auto pb-2">
          {categories.map((cat) => {
            const count = getProjectsByCategory(cat.id).length;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                  activeCategory === cat.id
                    ? `${cat.color} text-white`
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {cat.label} ({count})
              </button>
            );
          })}
        </div>
        
        {/* Project List */}
        <div className="flex-1 space-y-2 overflow-y-auto max-h-[300px] lg:max-h-none">
          {activeProjects.map((project, index) => (
            <div
              key={project.id}
              onClick={() => openGallery(project)}
              className="group flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl cursor-pointer transition-all duration-300 hover:bg-gray-800 text-white"
            >
              {/* Project Thumbnail */}
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0">
                <img 
                  src={project.afterImage} 
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-white text-sm sm:text-base truncate group-hover:text-blue-400 transition-colors">
                  {project.title}
                </h4>
                <p className="text-xs sm:text-sm text-gray-400 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  <span className="truncate">{project.location}</span>
                </p>
              </div>
              
              <div className="text-gray-500 text-xs sm:text-sm">
                {project.galleryImages?.length || 0}
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-700">
          <Button
            onClick={scrollToContact}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 sm:py-6 text-sm sm:text-base"
          >
            Start Your Project
          </Button>
        </div>
      </div>

      {/* Right Side - Project Gallery Preview */}
      <div className="w-full lg:w-2/3 bg-gray-100 p-4 sm:p-6 lg:p-8 flex flex-col min-h-[400px] lg:min-h-0">
        {activeProjects.length > 0 ? (
          <>
            {/* Category Header */}
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full ${activeCategoryData?.color}`}></div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{activeCategoryData?.label} Projects</h3>
              </div>
              <span className="text-xs sm:text-sm text-gray-500">{activeProjects.length} Projects</span>
            </div>
            
            {/* Project Grid */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 overflow-y-auto">
              {activeProjects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => openGallery(project)}
                  className="group relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <img
                    src={project.afterImage}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 sm:translate-y-4 sm:group-hover:translate-y-0 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300">
                    <h4 className="text-white font-bold text-sm sm:text-lg">{project.title}</h4>
                    <p className="text-gray-300 text-xs sm:text-sm flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{project.location}</span>
                    </p>
                  </div>
                  <div className="absolute top-3 right-3 sm:top-4 sm:right-4 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-1.5 sm:p-2">
                      <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-gray-400">
              <Folder className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 opacity-50" />
              <p className="text-sm sm:text-base">No projects in this category yet</p>
            </div>
          </div>
        )}
      </div>

      {/* Gallery Modal */}
      {selectedProject && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeGallery}
        >
          <div 
            className="relative max-w-6xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <Button
              onClick={closeGallery}
              className="absolute -top-12 right-0 bg-white/10 hover:bg-white/20 text-white rounded-full p-2"
              size="icon"
            >
              <X className="w-6 h-6" />
            </Button>

            {/* Project Title */}
            <div className="text-white text-center mb-4">
              <h3 className="text-2xl font-bold">{selectedProject.title}</h3>
              <p className="text-gray-400">
                {currentImageIndex + 1} / {selectedProject.galleryImages.length}
              </p>
            </div>

            {/* Main Image */}
            <div className="relative aspect-[16/10] bg-gray-900 rounded-lg overflow-hidden">
              <img
                src={selectedProject.galleryImages[currentImageIndex]}
                alt={`${selectedProject.title} - Photo ${currentImageIndex + 1}`}
                className="w-full h-full object-contain"
              />

              {/* Navigation Arrows */}
              <Button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full p-3"
                size="icon"
              >
                <ChevronLeft className="w-8 h-8" />
              </Button>
              <Button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full p-3"
                size="icon"
              >
                <ChevronRight className="w-8 h-8" />
              </Button>
            </div>

            {/* Thumbnail Strip */}
            <div className="flex gap-2 mt-4 overflow-x-auto pb-2 justify-center">
              {selectedProject.galleryImages.map((img, index) => (
                <button
                  key={`thumb-${img.slice(-20)}-${index}`}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    currentImageIndex === index 
                      ? 'border-blue-500 opacity-100' 
                      : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PortfolioProfessional;
